import React, { useState, useEffect } from 'react';
import { Button } from 'reactstrap';
import queryString from 'query-string';
import { programRequestsApi } from '../api/programRequestsApi';
import { InstitutionField } from '../shared/InstitutionField';
import { ProgramSelector } from '../shared/ProgramSelector';
import { ProgramSummaryCard } from '../shared/ProgramSummaryCard';
import {
    AdditionalCommentsField,
    commentsLengthError,
} from '../shared/AdditionalCommentsField';
import {
    TeachoutField,
    teachoutRequiredError,
    AllStudentsTaughtOutField,
    allStudentsTaughtOutError,
} from '../shared/TeachoutField';
import { createMergeProgramsModel } from './mergeProgramsModel';
import { createMergeProgramsFlow } from './createMergeProgramsFlow';

function hasValidationErrors(form) {
    if (
        !form.institutionId ||
        !form.sourceProgramId ||
        !form.destProgramId ||
        Boolean(teachoutRequiredError(form.teachoutFlag))
    ) {
        return true;
    }
    if (form.teachoutFlag === true) {
        return Boolean(allStudentsTaughtOutError(form.allStudentsTaughtOut));
    }
    return false;
}

export const MergeProgramsForm = ({
    onBack,
    onSuccess,
    institutionOptions: parentInstitutionOptions = [],
    programsByInstitution = {},
    maximumAdditionalCommentsLength,
    initialModel,
    submitFlow = createMergeProgramsFlow,
    mode = 'create',
    requestDetails,
}) => {
    const [form, setForm] = useState(
        () => initialModel ?? createMergeProgramsModel()
    );
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loadingPrograms, setLoadingPrograms] = useState(false);
    const [initializingEdit, setInitializingEdit] = useState(mode === 'edit');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (parentInstitutionOptions.length > 0) {
            setInstitutionOptions(parentInstitutionOptions);
        } else {
            programRequestsApi.getInstitutions().then(res => {
                const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
                setInstitutionOptions(mapped);
            });
        }
    }, []);

    useEffect(() => {
        if (institutionOptions.length === 1 && form.institutionId === null) {
            setForm(f => ({ ...f, institutionId: institutionOptions[0].value }));
        }
    }, [institutionOptions]);

    useEffect(() => {
        if (!form.institutionId) {
            setPrograms([]);
            setForm(f => ({ ...f, sourceProgramId: null, destProgramId: null }));
            setInitializingEdit(false);
            return;
        }
        if (mode !== 'edit') {
            setForm(f => ({ ...f, sourceProgramId: null, destProgramId: null }));
        }

        const cached = programsByInstitution[form.institutionId];
        if (Array.isArray(cached) && cached.length > 0) {
            setPrograms(cached);
            setInitializingEdit(false);
            return;
        }

        setLoadingPrograms(true);
        const query = queryString.stringify(
            { institutions: form.institutionId },
            { arrayFormat: 'separator', skipEmptyString: true },
        );
        programRequestsApi.getProgramsForInstitution(query)
            .then(res => setPrograms(res?.programs?.flat() ?? []))
            .finally(() => {
                setLoadingPrograms(false);
                setInitializingEdit(false);
            });
    }, [form.institutionId, mode]);

    const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const handleTeachoutFlagChange = teachoutFlag => {
        setForm(current => ({
            ...current,
            teachoutFlag,
            allStudentsTaughtOut: teachoutFlag === true ? current.allStudentsTaughtOut : null,
        }));
    };

    const sourceProgram = programs.find(p => p.programId === form.sourceProgramId) ?? null;
    const destProgram = programs.find(p => p.programId === form.destProgramId) ?? null;

    // Each selector hides the program already chosen in the other picker
    const sourceOptions = programs.filter(p => p.programId !== form.destProgramId);
    const destOptions = programs.filter(p => p.programId !== form.sourceProgramId);

    const handleSubmit = () => {
        setSubmitted(true);
        setSubmitError(null);
        if (
            hasValidationErrors(form) ||
            commentsLengthError(form.comments, maximumAdditionalCommentsLength)
        ) return;

        setSubmitting(true);

        const onError = err => {
            const message =
                err?.response?.data?.message ?? 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
            setSubmitting(false);
        };

        submitFlow({ form, details: requestDetails, onError }).then(result => {
            setSubmitting(false);
            if (!result) return;
            onSuccess();
        });
    };

    const err = msg => <p className="text-danger mt-1">{msg}</p>;

    if (mode === 'edit' && initializingEdit) {
        return (
            <p className="text-muted small text-center py-4 mb-0">
                Loading request details…
            </p>
        );
    }

    return (
        <div>
            {mode !== 'edit' && (
                <button type="button" className="btn btn-link p-0 mb-3" onClick={onBack}>
                    ← Back
                </button>
            )}

            <InstitutionField
                institutionOptions={institutionOptions}
                value={form.institutionId}
                onChange={val => setField('institutionId', val)}
            />
            {submitted && !form.institutionId && err('Institution is required.')}

            {loadingPrograms && (
                <p className="text-muted small mb-2">Loading programs…</p>
            )}

            <ProgramSelector
                inputId="merge-source-program"
                label="Program(s) merging into an existing program (Streamlining)"
                programs={sourceOptions}
                value={form.sourceProgramId}
                onChange={id => setField('sourceProgramId', id)}
            />
            {submitted && !form.sourceProgramId && err('Source program is required.')}

            <ProgramSelector
                inputId="merge-dest-program"
                label="Existing Program (Absorbing)"
                programs={destOptions}
                value={form.destProgramId}
                onChange={id => setField('destProgramId', id)}
            />
            {submitted && !form.destProgramId && err('Destination program is required.')}

            {sourceProgram && destProgram && (
                <>
                    <hr />
                    <div className="row no-gutters">
                        <div className="col-5">
                            <ProgramSummaryCard program={sourceProgram} heading="Merging" />
                        </div>
                        <div className="col-2 d-flex align-items-center justify-content-center">
                            <span className="text-muted" style={{ fontSize: '1.5rem' }}>→</span>
                        </div>
                        <div className="col-5">
                            <ProgramSummaryCard program={destProgram} heading="Into" />
                        </div>
                    </div>
                    <hr />
                </>
            )}

            <TeachoutField
                id="merge-teachout"
                value={form.teachoutFlag}
                onChange={handleTeachoutFlagChange}
                submitted={submitted}
            />
            {form.teachoutFlag === true && (
                <AllStudentsTaughtOutField
                    id="merge-all-taught-out"
                    value={form.allStudentsTaughtOut}
                    onChange={value => setField('allStudentsTaughtOut', value)}
                    submitted={submitted}
                />
            )}
            <p className="small text-muted">
                Will set: Status ={' '}
                {form.teachoutFlag === false ? 'Merged' :
                    form.teachoutFlag === true
                        ? (form.allStudentsTaughtOut === true ? 'Merged' :
                            form.allStudentsTaughtOut === false ? 'Suspended (until teach-out is complete)' :
                                '[select whether all students are taught out]')
                        : '[select teach-out]'}
            </p>

            <AdditionalCommentsField
                id="merge-comments"
                value={form.comments}
                onChange={value => setField('comments', value)}
                maximumLength={maximumAdditionalCommentsLength}
                submitted={submitted}
            />

            {submitError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {submitError}
                </div>
            )}

            <div className="d-flex justify-content-end mt-4">
                <Button color="secondary" className="mr-2" onClick={onBack} disabled={submitting}>
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting
                        ? mode === 'edit' ? 'Saving…' : 'Submitting…'
                        : mode === 'edit' ? 'Save Changes' : 'Submit Request'}
                </Button>
            </div>
        </div>
    );
};
